import { Injectable } from '@angular/core';
import {
  AssertEmptyComposantAttenduInterface,
  Campagne,
  CampagneUser,
  CampaignInfos,
  Composant,
  ComposantAttendu,
  DuplicateAndMoveInterface,
  Fichier,
  Patrimoine,
  RefetchAvancement,
  Valeur
} from '@get/api-interfaces';
import { RepositoryService } from '@get/services/repository';
import { EMPTY, Observable, catchError, first, map } from 'rxjs';
import { GeneratedCampagneApiService } from './campagne-api-generated.service';
import { environment } from '@get/environment';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class CampagneApiService extends GeneratedCampagneApiService {
  constructor(repo: RepositoryService, private readonly http: HttpClient) {
    super(repo);
  }

  public getCampagnePatrimoines(params: { idCampagne: number; params?: any }): Observable<Campagne> {
    return this.repo.getData<Campagne>('campagne/patrimoines/' + params.idCampagne, params.params);
  }

  public downloadOneFichier(idFichier: number): Observable<Blob> {
    return this.http
      .get(environment.apiUrl + `/campagne/fichier/download/${idFichier}`, {
        responseType: 'blob'
      })
      .pipe(first());
  }

  public sendResponsibleMail(params: {
    emails: string[];
    campaignInfos: CampaignInfos;
    launch: boolean;
  }): Observable<CampagneUser[]> {
    return this.repo.create<CampagneUser[]>(
      'campagne/send-responsible-mail/' + params.campaignInfos.idCampagne,
      params
    );
  }

  public refetchAvancement(ids: RefetchAvancement): Observable<Patrimoine[]> {
    return this.repo.create<Patrimoine[]>('campagne/refetch-avancement', ids);
  }

  public createOneComposant(composant: Partial<Composant>): Observable<Composant> {
    return this.repo.create<Composant>('campagne/composant', composant);
  }

  public createManyComposants(composants: Partial<Composant>[]): Observable<Composant[]> {
    return this.repo.create<Composant[]>('campagne/composant/many', composants);
  }

  public updateOneComposant(composant: Partial<Composant>): Observable<Composant> {
    return this.repo.update('campagne/composant/update', composant);
  }

  public duplicateComposant(idComposant: number, params?: { idCampagne?: number }): Observable<Composant> {
    return this.repo.create<Composant>('campagne/composant/duplicate/' + idComposant, null, params);
  }

  public duplicateAndMoveComposant(
    body: DuplicateAndMoveInterface[],
    params?: { idCampagne?: number }
  ): Observable<Composant[]> {
    return this.repo.create<Composant[]>('campagne/composant/duplicate-and-move/' + body[0].idComposant, body, params);
  }

  public updateValeurReturnComposant(
    valeur: Partial<Valeur>,
    params?: { idCampagne?: number }
  ): Observable<{ composant: Composant; composantAttendus: ComposantAttendu[] }> {
    return this.repo.update('campagne/valeur/return-composant', valeur, params);
  }

  public updateValeursReturnComposants(
    valeurs: Partial<Valeur>[],
    params?: { idCampagne?: number }
  ): Observable<{ composants: Composant[]; composantAttendus: ComposantAttendu[] }> {
    return this.repo.update('campagne/valeur/many-return-composants', valeurs, params);
  }

  public moveComposant(
    composant: Partial<Composant>
  ): Observable<{ composant: Composant; composantAttendus: ComposantAttendu[] }> {
    return this.repo.update('campagne/composant/move', composant);
  }

  public updateValeur(valeur: Partial<Valeur>): Observable<Valeur> {
    return this.repo.update('campagne/valeur', valeur);
  }

  public assertEmptyComposantAttendu(params: AssertEmptyComposantAttenduInterface): Observable<any> {
    return this.repo.update('campagne/composant-attendu/assert-empty', params);
  }

  public assertEmptyTree(params: AssertEmptyComposantAttenduInterface): Observable<any> {
    return this.repo.update('campagne/composant-attendu/assert-empty-tree', params);
  }

  public uploadManyImages(
    files: Array<File>,
    queryParams: { idOrganisation?: number } = {}
  ): Observable<Array<Fichier>> {
    const formData = new FormData();
    const route = `${new URL(environment.apiUrl + '/campagne/composant/images').href}${
      queryParams?.idOrganisation ? '?idOrganisation=' + queryParams.idOrganisation : ''
    }`;
    files.forEach(media => {
      formData.append('files', media);
    });

    return this.http.post(route, formData).pipe(
      first(),
      map((res: any) => res.data),
      catchError(() => EMPTY)
    );
  }

  public uploadMany(files: Array<File>, queryParams: { idOrganisation?: number } = {}): Observable<Array<Fichier>> {
    const formData = new FormData();
    const route = `${new URL(environment.apiUrl + '/campagne/composant/files').href}${
      queryParams?.idOrganisation ? '?idOrganisation=' + queryParams.idOrganisation : ''
    }`;
    files.forEach(media => {
      formData.append('files', media);
    });

    return this.http.post(route, formData).pipe(
      first(),
      map((res: any) => res.data),
      catchError(() => EMPTY)
    );
  }

  public deleteComposantWithComposantAttendu(
    idComposant: number
  ): Observable<{ affectedRows: number; composantAttendus: ComposantAttendu[] }> {
    return this.repo.delete('campagne/composant/with-composant-attendu/' + +idComposant);
  }

  public deleteValeurFichier(idValeur: number): Observable<number> {
    return this.repo.delete('campagne/file/' + +idValeur);
  }
}
